import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  IconButton,
  Box,
  Divider,
  useToast,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  useColorModeValue,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Textarea,
  Avatar,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Link,
  Select as ChakraSelect,
  Switch,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiPhone,
  FiMail,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiStar,
  FiMessageSquare,
  FiUser,
  FiPlus,
  FiExternalLink,
  FiSave,
  FiX,
  FiBriefcase,
  FiMapPin,
  FiBarChart2,
  FiGlobe,
  FiDatabase,
  FiZap,
  FiHelpCircle,
  FiFolder,
  FiClipboard,
  FiCalendar,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { useContactDeals } from '../../hooks/useContactDeals';
import { useContactRelationships } from '../../hooks/useContactRelationships';
import ContactTypeBadges from './ContactTypeBadges';
import UnifiedActivityTimeline from './UnifiedActivityTimeline';
import StageBadge from './StageBadge';
import AddNetworkRelationshipModal from './AddNetworkRelationshipModal';
import QuickAddDeal from './QuickAddDeal';
import ContactNotes from './ContactNotes';
import TagSelector from './TagSelector';
import ImportedDataCard from './ImportedDataCard';
import RetrTabPanel from './RetrTabPanel';
import TaskModal from '../Tasks/TaskModal';
import LogActivityModal from './LogActivityModal';
import DialpadSMSButton from './Dialpad/DialpadSMSButton';
import EmailTemplateModal from './Actions/EmailTemplateModal';
import ContactCampaignPanel from './Campaigns/ContactCampaignPanel';
import DocumentsPanel from './Documents/DocumentsPanel';
import { purgeEntityDocuments } from '../../hooks/useCrmDocuments';
import { Select, CreatableSelect } from 'chakra-react-select';
import moment from 'moment';
import { db, functionsUsEast1 } from '../../utils/init-firebase';
import { httpsCallable } from 'firebase/functions';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import { buildPublicBookingUrl } from '../../constants/calScheduling';
import { computeDnc, toDncColumns, dncSummary, isChannelBlocked } from '../../utils/dnc';
import { setContactTags } from '../../utils/crmTags';
import { useTagRemovalConfirm } from './ConfirmTagRemovalDialog';
import { useTasks } from '../../hooks/useTasks';
import { useTourActions, useAutoTour, useFeatureTour } from '../FeatureTour/FeatureTourProvider';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';

// FIXED: Module-level constants for empty arrays/objects to prevent new references
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const DEFAULT_CONTACT_TYPES = ['borrower'];

// Documents is appended AFTER Campaigns (index 5) so the FeatureTour actions
// that hard-code indices 0-5 keep working; RETR/Edit shift down when shown.
// The tab is currently ADMIN-ONLY (hidden for regular CRM users) — when it
// renders it is always index 6, so this constant only applies to admins.
const DOCUMENTS_TAB_INDEX = 6;

// Overview tab field: label over value, with a muted em dash when empty so
// every field is always visible in a predictable spot.
function OverviewField({ label, span, children }) {
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const emptyColor = useColorModeValue('gray.400', 'gray.500');
  const isEmpty = children === null || children === undefined || children === '' || children === false;
  return (
    <Box gridColumn={span === 2 ? { base: 'auto', sm: 'span 2' } : undefined} minW={0}>
      <Text fontSize="xs" fontWeight="semibold" color={labelColor}>{label}</Text>
      {isEmpty ? (
        <Text color={emptyColor}>—</Text>
      ) : (typeof children === 'string' || typeof children === 'number') ? (
        <Text wordBreak="break-word">{children}</Text>
      ) : (
        children
      )}
    </Box>
  );
}

// Formats Firestore Timestamps, {seconds} objects, Dates, and ISO strings;
// returns null (→ em dash) when the value is missing or unparseable.
const formatContactDate = (value, fmt = 'MMM D, YYYY') => {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : value.seconds ? new Date(value.seconds * 1000) : value;
  const m = moment(date);
  return m.isValid() ? m.format(fmt) : null;
};

/** Order-insensitive comparison of two tag lists. */
function sameTagSet(a = [], b = []) {
  const left = a || [];
  const right = b || [];
  if (left.length !== right.length) return false;
  const set = new Set(left);
  return right.every(value => set.has(value));
}

/**
 * Contact Detail Drawer
 * 
 * Comprehensive 360-degree view of a contact including:
 * - Contact information
 * - Associated deals (as borrower and as related party)
 * - Unified activity timeline across all deals
 * - Network relationships
 * - Edit capabilities
 */
export default function ContactDetailDrawer({ 
  isOpen, 
  onClose, 
  contact: initialContact,
  contactId,
  onContactUpdated,
  onContactDeleted,
  crmUsers = [],
}) {
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const {
    contactTypes: configContactTypes,
    getVisibleTags,
    getTag,
    contactSources: configContactSources,
    customContactFields,
    loading: settingsLoading,
  } = useCrmSettings();

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const { confirmTagRemoval, dialog: removalDialog } = useTagRemovalConfirm();
  const editableTagOptions = useMemo(
    () => (getVisibleTags(currentUser?.uid, isCRMAdmin) || [])
      .map(tag => ({ value: tag.value, label: tag.label })),
    [getVisibleTags, currentUser?.uid, isCRMAdmin]
  );

  // crmUsers is now received as a prop from the parent (DashboardPage)

  const [contact, setContact] = useState(initialContact);

  const ownerName = contact?.ownerName || (contact?.ownerId ? crmUsers.find(u => u.uid === contact.ownerId)?.name : null) || 'Unassigned';
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Edited state
  const [editedData, setEditedData] = useState({});

  // Network relationship modal
  const { isOpen: isAddNetworkOpen, onOpen: onAddNetworkOpen, onClose: onAddNetworkClose } = useDisclosure();
  const { isOpen: isQuickAddDealOpen, onOpen: onQuickAddDealOpen, onClose: onQuickAddDealClose } = useDisclosure();
  const { isOpen: isTaskModalOpen, onOpen: onTaskModalOpen, onClose: onTaskModalClose } = useDisclosure();
  const { isOpen: isEmailModalOpen, onOpen: onEmailModalOpen, onClose: onEmailModalClose } = useDisclosure();
  const { isOpen: isLogActivityOpen, onOpen: onLogActivityOpen, onClose: onLogActivityClose } = useDisclosure();

  // Bumped after a manual activity log so UnifiedActivityTimeline refetches
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Per-user Dialpad availability (same gate as DealPage): user must be both
  // capable AND have the integration enabled on their profile.
  const [dialpadEnabled, setDialpadEnabled] = useState(false);
  // Cal.diy scheduling pilot gate (per-user flag on users/{uid})
  const [calSchedulingEnabled, setCalSchedulingEnabled] = useState(false);
  useEffect(() => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setDialpadEnabled(userData.dialpadCapable === true && userData.dialpadEnabled === true);
        setCalSchedulingEnabled(userData.calSchedulingEnabled === true);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Scheduling pilot: the LO's preferred booking page (default first, else
  // oldest active) powers the "Book Meeting" / "Send Booking Link" actions.
  const [bookingPage, setBookingPage] = useState(null);
  useEffect(() => {
    if (!calSchedulingEnabled || !currentUser?.uid || !isSupabaseConfigured()) {
      setBookingPage(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('crm_booking_pages')
        .select('id, owner_handle, slug, title, is_default')
        .eq('owner_id', currentUser.uid)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setBookingPage(data || null);
    })();
    return () => { cancelled = true; };
  }, [calSchedulingEnabled, currentUser?.uid]);

  // FeatureTour: the drawer opens WITHOUT a route change (local state on the
  // host page), so its tour is auto-started here rather than by navigation.
  // Tab indices 0-5 are fixed; RETR/Edit only append after them.
  const { startTour } = useFeatureTour();
  useTourActions(useMemo(() => ({
    showDealsTab: () => setSelectedTab(1),
    showActivityTab: () => setSelectedTab(2),
    showNotesTab: () => setSelectedTab(4),
    showCampaignsTab: () => setSelectedTab(5),
  }), []));
  useAutoTour(isOpen && !!contact, 'contact-drawer');

  // Load contact deals and relationships
  const { deals, loading: dealsLoading, asBorrower, asRelatedParty } = useContactDeals(contactId);
  const { relationships, loading: relationshipsLoading, removeRelationship, refetch: refetchRelationships } = useContactRelationships(contactId);
  
  // Load tasks for this user
  const { createTask } = useTasks(currentUser?.uid, { type: 'crm', status: 'pending' });
  
  // Separate deals into borrower and party roles
  const borrowerDeals = asBorrower || [];
  const relatedPartyDeals = asRelatedParty || [];
  
  // Debug logging removed - uncomment below if needed for debugging
  // useEffect(() => {
  //   if (contactId && !dealsLoading && deals.length === 0) {
  //     console.log('[ContactDetailDrawer] No deals for contact:', contactId);
  //   }
  // }, [contactId, dealsLoading, deals]);

  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBG = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statBG = useColorModeValue('white', 'gray.700');
  const infoBG = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Show RETR tab when the contact has mortgage-broker type or any RETR data
  const showRetrTab = contact && (
    contact.retrProfileUrl || contact.retrLoanCount || contact.retrTotalLoanCount ||
    contact.retrNmlsId || contact.retrProfileId ||
    (contact.contactTypes || []).some(t => t.toLowerCase().includes('broker') || t.toLowerCase().includes('mortgage'))
  );

  // The parent may hand us a fresher record (or a newly created one) after mount,
  // so adopt it instead of keeping the object captured by useState.
  useEffect(() => {
    if (initialContact) {
      setContact(initialContact);
    }
  }, [initialContact]);

  // extra_fields isn't part of view_contact_summary (list rows), so fetch it
  // lazily for the open contact to power the Imported Data card.
  useEffect(() => {
    const id = initialContact?.id;
    const alreadyLoaded = initialContact?.extraFields !== undefined && initialContact?.extraFields !== null;
    if (!id || alreadyLoaded || !isSupabaseConfigured()) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('crm_contacts')
        .select('extra_fields')
        .eq('id', id)
        .maybeSingle();
      if (!cancelled && !error && data?.extra_fields && Object.keys(data.extra_fields).length > 0) {
        setContact(prev => (prev && prev.id === id ? { ...prev, extraFields: data.extra_fields } : prev));
      }
    })();
    return () => { cancelled = true; };
  }, [initialContact?.id]);

  // FIXED: Initialize edited data with stable object/array references
  useEffect(() => {
    if (contact) {
      setEditedData({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        middleInitial: contact.middleInitial || '',
        email: contact.email || '',
        phone: contact.phone || '',
        secondaryEmail: contact.secondaryEmail || '',
        secondaryPhone: contact.secondaryPhone || '',
        contactTypes: contact.contactTypes || DEFAULT_CONTACT_TYPES,
        primaryContactType: contact.primaryContactType || contact.contactTypes?.[0] || 'borrower',
        tags: contact.tags || EMPTY_ARRAY,
        source: contact.source || 'manual',
        company: contact.company || '',
        jobTitle: contact.jobTitle || '',
        licenseNumber: contact.licenseNumber || '',
        licenseState: contact.licenseState || '',
        website: contact.website || '',
        officeAddress: contact.officeAddress || '',
        preferredContactMethod: contact.preferredContactMethod || '',
        birthday: contact.birthday ? moment(contact.birthday.toDate ? contact.birthday.toDate() : contact.birthday).format('YYYY-MM-DD') : '',
        dnc: contact.dnc || false,
        dncCall: contact.dncCall || false,
        dncEmail: contact.dncEmail || false,
        dncSms: contact.dncSms || false,
        isActive: contact.isActive !== undefined ? contact.isActive : true,
        isFavorite: contact.isFavorite || false,
        customFields: contact.customFields || EMPTY_OBJECT,
        // Mailing Address (contact's residence, NOT deal property)
        mailingAddress: {
          street: contact.mailingAddress?.street || '',
          city: contact.mailingAddress?.city || '',
          state: contact.mailingAddress?.state || '',
          zip: contact.mailingAddress?.zip || '',
        },
        // RETR Recruiting Intelligence
        retrProfileUrl: contact.retrProfileUrl || '',
        retrLoType: contact.retrLoType || '',
        retrState: contact.retrState || '',
        retrLoanCount: contact.retrLoanCount ?? '',
        retrLoanVolume: contact.retrLoanVolume ?? '',
        retrConvCount: contact.retrConvCount ?? '',
        retrVaCount: contact.retrVaCount ?? '',
        retrFhaCount: contact.retrFhaCount ?? '',
        retrTotalLoanCount: contact.retrTotalLoanCount ?? '',
        retrTotalLoanVolume: contact.retrTotalLoanVolume ?? '',
        retrAvgLoan: contact.retrAvgLoan ?? '',
        retrLocation: contact.retrLocation || '',
      });
    }
  }, [contact]);

  const handleSave = async () => {
    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    // Deselecting a tag in the form only stages the removal, so confirm the
    // whole set once here rather than interrupting every deselect. Cancelling
    // abandons the entire save so nothing is half-applied and the user can
    // re-add the tag before trying again.
    const removedTags = (contact.tags || []).filter(
      tag => !(editedData.tags || []).includes(tag)
    );

    if (removedTags.length > 0) {
      const confirmed = await confirmTagRemoval({
        title: removedTags.length === 1 ? 'Remove this tag?' : `Remove ${removedTags.length} tags?`,
        tags: removedTags.map(tag => getTag(tag)?.label || tag),
        description: 'Saving will remove these tags from this contact.',
        note: "They are also removed from this contact's deals.",
        confirmLabel: 'Save and remove',
      });

      if (!confirmed) return;
    }

    setIsSaving(true);
    try {
      const tagsChanged = !sameTagSet(editedData.tags, contact.tags);

      // Transform camelCase editedData to snake_case for Supabase
      // Only include fields that exist in the database schema
      const updateData = {
        // Core Identity (in schema)
        first_name: editedData.firstName || null,
        last_name: editedData.lastName || null,
        email: editedData.email ? editedData.email.toLowerCase() : null,
        phone: editedData.phone || null,
        birthday: editedData.birthday || null,
        // Professional (in schema)
        company: editedData.company || null,
        job_title: editedData.jobTitle || null,
        // Classification (in schema)
        source: editedData.source || null,
        contact_types: editedData.contactTypes || [],
        // `tags` is deliberately absent: it is written separately below so this
        // payload can never overwrite tags the form did not load.
        // Status (in schema) — DNC 4-category (master + per-channel)
        ...toDncColumns({ dnc: editedData.dnc, dncCall: editedData.dncCall, dncEmail: editedData.dncEmail, dncSms: editedData.dncSms }),
        is_active: editedData.isActive !== undefined ? editedData.isActive : true,
        is_favorite: editedData.isFavorite || false,
        // Mailing Address (added via patch)
        mailing_street: editedData.mailingAddress?.street || null,
        mailing_city: editedData.mailingAddress?.city || null,
        mailing_state: editedData.mailingAddress?.state || null,
        mailing_zip: editedData.mailingAddress?.zip || null,
        // Extended fields (added via patch)
        middle_initial: editedData.middleInitial || null,
        secondary_email: editedData.secondaryEmail || null,
        secondary_phone: editedData.secondaryPhone || null,
        primary_contact_type: editedData.primaryContactType || null,
        license_number: editedData.licenseNumber || null,
        license_state: editedData.licenseState || null,
        website: editedData.website || null,
        office_address: editedData.officeAddress || null,
        preferred_contact_method: editedData.preferredContactMethod || null,
        // Metadata
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('crm_contacts')
        .update(updateData)
        .eq('id', contact.id);

      if (error) throw error;

      let savedTags = contact.tags;
      if (tagsChanged) {
        savedTags = await setContactTags({
          contactId: contact.id,
          tags: editedData.tags || [],
          actorId: currentUser?.uid,
        });
      }

      try {
        const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
        const trackFields = ['email', 'phone', 'source', 'doNotContact', 'isActive', 'company', 'jobTitle'];
        for (const field of trackFields) {
          if (editedData[field] !== undefined && editedData[field] !== contact[field]) {
            fireTrigger({ eventType: 'contact.updated', payload: { contactId: contact.id, fieldName: field } });
            break;
          }
        }
      } catch (_) {}

      toast({
        title: 'Contact updated',
        status: 'success',
        duration: 2000,
      });

      // Update local state immediately with ALL fields
      const updatedContact = {
        ...contact,
        ...editedData,
        tags: savedTags || EMPTY_ARRAY,
        // Ensure nested objects are properly merged
        mailingAddress: {
          street: editedData.mailingAddress?.street || '',
          city: editedData.mailingAddress?.city || '',
          state: editedData.mailingAddress?.state || '',
          zip: editedData.mailingAddress?.zip || '',
        },
      };
      setContact(updatedContact);
      
      if (onContactUpdated) {
        onContactUpdated(updatedContact);
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: 'Error updating contact',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      return;
    }

    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    setIsDeleting(true);
    try {
      // First check if contact has associated deals
      const { data: deals, error: dealsError } = await supabase
        .from('crm_deals')
        .select('id')
        .eq('contact_id', contact.id)
        .limit(1);

      if (dealsError) throw dealsError;

      if (deals && deals.length > 0) {
        toast({
          title: 'Cannot delete contact',
          description: 'Contact has associated deals. Delete or reassign deals first.',
          status: 'error',
          duration: 5000,
        });
        setIsDeleting(false);
        return;
      }

      // Also block when the contact is a related party on any deal
      // (buyer agent, title agent, etc. -- rows in crm_deal_participants)
      const { data: partyRows, error: partyError } = await supabase
        .from('crm_deal_participants')
        .select('id')
        .eq('contact_id', contact.id)
        .limit(1);

      if (partyError) throw partyError;

      if (partyRows && partyRows.length > 0) {
        toast({
          title: 'Cannot delete contact',
          description: 'Contact is a related party on one or more deals. Remove them from those deals first.',
          status: 'error',
          duration: 5000,
        });
        setIsDeleting(false);
        return;
      }

      // Purge stored documents BEFORE the contact row disappears — the
      // backend ownership check needs the contact to still exist. not-found
      // (nothing to purge / callable unavailable) never blocks deletion.
      const purgeResult = await purgeEntityDocuments('contact', contact.id);
      if (!purgeResult.success && purgeResult.code !== 'functions/not-found') {
        toast({
          title: 'Could not delete contact documents',
          description: `${purgeResult.error} — the contact was not deleted.`,
          status: 'error',
          duration: 6000,
        });
        setIsDeleting(false);
        return;
      }

      // Delete the contact
      const { error } = await supabase
        .from('crm_contacts')
        .delete()
        .eq('id', contact.id);

      if (error) throw error;

      toast({
        title: 'Contact deleted',
        status: 'success',
        duration: 2000,
      });

      if (onContactDeleted) {
        onContactDeleted(contact.id);
      }
      onClose();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: 'Error deleting contact',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Admin-only: flip a contact between a personal record and a company record
  // (record_scope='company' → visible to every CRM admin via the visibility
  // predicate). _updated_by='import' suppresses the contact.updated audit
  // event — scope flips are administrative bookkeeping, not contact activity,
  // and must never enroll anyone in automations.
  const handleToggleCompanyScope = async () => {
    if (!isSupabaseConfigured() || !isCRMAdmin || !contact?.id) return;

    const previousScope = contact.recordScope || 'user';
    const newScope = previousScope === 'company' ? 'user' : 'company';

    // Optimistic update
    setContact({ ...contact, recordScope: newScope });

    try {
      const { error } = await supabase
        .from('crm_contacts')
        .update({ record_scope: newScope, _updated_by: 'import' })
        .eq('id', contact.id);

      if (error) {
        setContact({ ...contact, recordScope: previousScope });
        throw error;
      }

      if (onContactUpdated) {
        onContactUpdated({ ...contact, recordScope: newScope });
      }

      toast({
        title: newScope === 'company' ? 'Marked as company record' : 'Converted to personal record',
        description: newScope === 'company'
          ? 'All CRM admins can now see this contact.'
          : 'Visibility is back to the owner (plus any shares).',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error toggling record scope:', error);
      toast({
        title: 'Error updating record scope',
        description: error.message,
        status: 'error',
        duration: 4000,
      });
    }
  };

  const handleToggleFavorite = async () => {
    if (!isSupabaseConfigured()) return;

    const newFavorite = !contact.isFavorite;
    
    // Optimistic update
    setContact({ ...contact, isFavorite: newFavorite });
    
    try {
      const { error } = await supabase
        .from('crm_contacts')
        .update({ is_favorite: newFavorite })
        .eq('id', contact.id);

      if (error) {
        // Rollback on error
        setContact({ ...contact, isFavorite: !newFavorite });
        throw error;
      }
      
      toast({
        title: newFavorite ? 'Added to favorites' : 'Removed from favorites',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Error updating favorite',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSaveTask = async (taskId, taskData) => {
    try {
      const result = await createTask(taskData);
      if (result.success) {
        toast({
          title: 'Task created',
          status: 'success',
          duration: 2000,
        });
        return result;
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Error creating task:', error);
      return { success: false, error: error.message };
    }
  };

  if (!contact) {
    return null;
  }

  const stats = contact.stats || {};
  const fullName = `${contact.firstName} ${contact.middleInitial ? `${contact.middleInitial}. ` : ''}${contact.lastName}`;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton zIndex={10} top="24px" right="24px" />
        <Tooltip label="Show tutorial" hasArrow>
          <IconButton
            aria-label="Show tutorial"
            icon={<FiHelpCircle />}
            size="sm"
            variant="ghost"
            position="absolute"
            zIndex={10}
            top="24px"
            right="64px"
            onClick={() => startTour('contact-drawer', { source: 'manual' })}
          />
        </Tooltip>
        {!isEditing && (
          <Tooltip label="Edit contact" hasArrow>
            <IconButton
              aria-label="Edit contact"
              icon={<FiEdit2 />}
              size="sm"
              variant="ghost"
              position="absolute"
              zIndex={10}
              top="24px"
              right="104px"
              onClick={() => {
                setIsEditing(true);
                // Edit is the last tab: after Campaigns (5) plus the
                // conditional Documents (admin-only) and RETR tabs.
                setSelectedTab(6 + (isCRMAdmin ? 1 : 0) + (showRetrTab ? 1 : 0));
              }}
            />
          </Tooltip>
        )}
        
        {/* Header */}
        <DrawerHeader bg={headerBG} pb={4} pt={6}>
          {/* Admin: Show Assigned To */}
          {isCRMAdmin && (
            <HStack mb={2} spacing={2}>
              <Badge colorScheme="purple" variant="solid" fontSize="xs">
                Assigned To: {ownerName}
              </Badge>
            </HStack>
          )}
          <VStack align="stretch" spacing={3}>
            {/* Two Column Header Layout */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              {/* Column 1: Contact Info */}
              <GridItem>
                <VStack align="flex-start" spacing={2}>
                  {/* Name Row */}
                  {isEditing ? (
                    <HStack>
                      <Input
                        value={editedData.firstName}
                        onChange={(e) => setEditedData({ ...editedData, firstName: e.target.value })}
                        placeholder="First"
                        size="md"
                        bg="white"
                        maxW="150px"
                      />
                      <Input
                        value={editedData.middleInitial}
                        onChange={(e) => setEditedData({ ...editedData, middleInitial: e.target.value.toUpperCase() })}
                        placeholder="MI"
                        size="md"
                        bg="white"
                        maxW="50px"
                        maxLength={1}
                      />
                      <Input
                        value={editedData.lastName}
                        onChange={(e) => setEditedData({ ...editedData, lastName: e.target.value })}
                        placeholder="Last"
                        size="md"
                        bg="white"
                        maxW="150px"
                      />
                    </HStack>
                  ) : (
                    <>
                      <HStack spacing={2} data-tour="contact-header">
                        <Heading size="lg">{fullName}</Heading>
                        <Tooltip label={contact.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                          <IconButton
                            icon={<FiStar />}
                            size="sm"
                            variant="ghost"
                            color={contact.isFavorite ? 'yellow.400' : 'gray.400'}
                            aria-label="Toggle favorite"
                            onClick={handleToggleFavorite}
                          />
                        </Tooltip>
                      </HStack>
                      <ContactTypeBadges
                        contactTypes={contact.contactTypes || []}
                        primaryContactType={contact.primaryContactType}
                        showIcons={true}
                      />
                    </>
                  )}

                  {/* Action Buttons Row */}
                  <HStack spacing={2}>
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsEditing(false);
                            // Reset edited data
                            setEditedData({
                              firstName: contact.firstName || '',
                              lastName: contact.lastName || '',
                              middleInitial: contact.middleInitial || '',
                              email: contact.email || '',
                              phone: contact.phone || '',
                              secondaryEmail: contact.secondaryEmail || '',
                              secondaryPhone: contact.secondaryPhone || '',
                              contactTypes: contact.contactTypes || ['borrower'],
                              primaryContactType: contact.primaryContactType || 'borrower',
                              tags: contact.tags || [],
                              source: contact.source || 'manual',
                              company: contact.company || '',
                              jobTitle: contact.jobTitle || '',
                              licenseNumber: contact.licenseNumber || '',
                              licenseState: contact.licenseState || '',
                              website: contact.website || '',
                              officeAddress: contact.officeAddress || '',
                              preferredContactMethod: contact.preferredContactMethod || '',
                              dnc: contact.dnc || false,
                              isActive: contact.isActive !== undefined ? contact.isActive : true,
                              isFavorite: contact.isFavorite || false,
                              customFields: contact.customFields || {},
                              mailingAddress: {
                                street: contact.mailingAddress?.street || '',
                                city: contact.mailingAddress?.city || '',
                                state: contact.mailingAddress?.state || '',
                                zip: contact.mailingAddress?.zip || '',
                              },
                              retrProfileUrl: contact.retrProfileUrl || '',
                              retrLoType: contact.retrLoType || '',
                              retrState: contact.retrState || '',
                              retrLoanCount: contact.retrLoanCount ?? '',
                              retrLoanVolume: contact.retrLoanVolume ?? '',
                              retrConvCount: contact.retrConvCount ?? '',
                              retrVaCount: contact.retrVaCount ?? '',
                              retrFhaCount: contact.retrFhaCount ?? '',
                              retrTotalLoanCount: contact.retrTotalLoanCount ?? '',
                              retrTotalLoanVolume: contact.retrTotalLoanVolume ?? '',
                              retrAvgLoan: contact.retrAvgLoan ?? '',
                              retrLocation: contact.retrLocation || '',
                            });
                          }}
                          leftIcon={<FiX />}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleSave}
                          isLoading={isSaving}
                          leftIcon={<FiSave />}
                        >
                          Save Changes
                        </Button>
                      </>
                    ) : (
                      <>
                        <Tooltip label="Delete contact">
                          <IconButton
                            icon={<FiTrash2 />}
                            aria-label="Delete contact"
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={handleDelete}
                            isLoading={isDeleting}
                          />
                        </Tooltip>
                      </>
                    )}
                  </HStack>

                  {/* Contact Info (Email, Phone, Company) */}
                  {!isEditing && (
                    <Box data-tour="contact-info">
                      <VStack align="flex-start" spacing={2}>
                        <HStack spacing={4} flexWrap="wrap">
                          {contact.email && (
                            <HStack spacing={2}>
                              <Icon as={FiMail} boxSize={4} color="gray.500" />
                              <Link href={`mailto:${contact.email}`} fontSize="sm">
                                {contact.email}
                              </Link>
                            </HStack>
                          )}
                          {contact.phone && (
                            <HStack spacing={2}>
                              <Icon as={FiPhone} boxSize={4} color="gray.500" />
                              <Link href={`tel:${contact.phone}`} fontSize="sm">
                                {contact.phone}
                              </Link>
                            </HStack>
                          )}
                          {contact.company && (
                            <HStack spacing={2}>
                              <Icon as={FiBriefcase} boxSize={4} color="gray.500" />
                              <Text fontSize="sm">{contact.company}</Text>
                            </HStack>
                          )}
                        </HStack>

                        {(contact.mailingAddress?.street || contact.mailingAddress?.city) && (
                          <HStack spacing={2}>
                            <Icon as={FiMapPin} boxSize={4} color="gray.500" />
                            <Text fontSize="xs" color="gray.600">
                              {contact.mailingAddress?.street && `${contact.mailingAddress.street}, `}
                              {contact.mailingAddress?.city && `${contact.mailingAddress.city}, `}
                              {contact.mailingAddress?.state || ''} {contact.mailingAddress?.zip || ''}
                            </Text>
                          </HStack>
                        )}

                        {(contact.dnc || contact.dncCall || contact.dncEmail || contact.dncSms) && (
                          <Badge colorScheme="red" fontSize="sm">
                            {contact.dnc ? 'DO NOT CONTACT' : `DNC: ${dncSummary(contact)}`}
                          </Badge>
                        )}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </GridItem>

              {/* Column 2: Tags */}
              {!isEditing && (
                <GridItem>
                  <VStack align="flex-start" spacing={2} h="full" data-tour="contact-tags">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                      Tags
                    </Text>
                    <TagSelector
                      contactId={contactId}
                      currentTags={contact.tags || []}
                      userId={currentUser?.uid}
                      isEditable={true}
                      onTagsChange={(newTags) => setContact(prev => ({ ...prev, tags: newTags }))}
                    />
                  </VStack>
                </GridItem>
              )}
            </Grid>

            {/* Quick Stats - Full Width */}
            <Grid templateColumns="repeat(4, 1fr)" gap={3} mt={2} data-tour="contact-stats">
              <GridItem>
                <Box p={2} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">Total Deals</StatLabel>
                    <StatNumber fontSize="lg">{stats.totalDeals || 0}</StatNumber>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box p={2} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">As Borrower</StatLabel>
                    <StatNumber fontSize="lg">{stats.totalDealsAsBorrower || 0}</StatNumber>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box p={2} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">As Party</StatLabel>
                    <StatNumber fontSize="lg">{stats.totalDealsAsRelatedParty || 0}</StatNumber>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box p={2} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">Lifetime Value</StatLabel>
                    <StatNumber fontSize="md">
                      ${((stats.lifetimeValue || 0) / 1000).toFixed(0)}K
                    </StatNumber>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>

            {/* Quick Actions (2-Click Rule) */}
            {!isEditing && (
              <HStack spacing={2} rowGap={2} pt={2} flexWrap="wrap" data-tour="contact-quick-actions">
                <Button
                  size="sm"
                  leftIcon={<FiPhone />}
                  colorScheme="blue"
                  isDisabled={!contact.phone || contact.dnc}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `tel:${contact.phone}`;
                  }}
                  flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                >
                  Call
                </Button>
                {dialpadEnabled && (
                  <Box flex={{ base: '1 1 45%', sm: '0 1 auto' }}>
                    <DialpadSMSButton
                      phoneNumber={contact.phone}
                      contactName={`${contact.firstName} ${contact.lastName}`}
                      contactId={contact.id}
                      contact={contact}
                      isDisabled={isChannelBlocked(contact, 'sms')}
                      size="sm"
                    />
                  </Box>
                )}
                {calSchedulingEnabled && bookingPage && (
                  <>
                    <Button
                      size="sm"
                      leftIcon={<FiCalendar />}
                      colorScheme="cyan"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          buildPublicBookingUrl(bookingPage.owner_handle, bookingPage.slug, {
                            name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
                            email: contact.email || '',
                            phone: contact.phone || '',
                          }),
                          '_blank',
                          'noopener'
                        );
                      }}
                      flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                    >
                      Book Meeting
                    </Button>
                    {dialpadEnabled && contact.phone && (
                      <Box flex={{ base: '1 1 45%', sm: '0 1 auto' }}>
                        <DialpadSMSButton
                          phoneNumber={contact.phone}
                          contactName={`${contact.firstName} ${contact.lastName}`}
                          contactId={contact.id}
                          contact={contact}
                          isDisabled={isChannelBlocked(contact, 'sms')}
                          size="sm"
                          buttonLabel="Send Booking Link"
                          buttonColorScheme="cyan"
                          initialMessage={`Hi ${contact.firstName || 'there'}! Grab a time that works for you here: ${buildPublicBookingUrl(bookingPage.owner_handle, bookingPage.slug, {
                            name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
                            email: contact.email || '',
                          })}`}
                        />
                      </Box>
                    )}
                  </>
                )}
                <Button
                  size="sm"
                  leftIcon={<FiMail />}
                  colorScheme="purple"
                  isDisabled={!contact.email || contact.dnc}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEmailModalOpen();
                  }}
                  flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                >
                  Email
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FiClipboard />}
                  colorScheme="teal"
                  onClick={onLogActivityOpen}
                  flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                  data-tour="contact-log-activity"
                >
                  Log Activity
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FiPlus />}
                  colorScheme="green"
                  onClick={onQuickAddDealOpen}
                  flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                >
                  Add Deal
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FiPlus />}
                  colorScheme="orange"
                  onClick={onTaskModalOpen}
                  flex={{ base: '1 1 45%', sm: '0 1 auto' }}
                >
                  Add Task
                </Button>
              </HStack>
            )}
          </VStack>
        </DrawerHeader>

        {/* Tabs */}
        <DrawerBody px={0}>
          <Tabs index={selectedTab} onChange={setSelectedTab} colorScheme="blue">
            <TabList px={6}>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiUser} />
                  <Text>Overview</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiBriefcase} />
                  <Text>Deals</Text>
                  {deals.length > 0 && (
                    <Badge colorScheme="blue" borderRadius="full">
                      {deals.length}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiMessageSquare} />
                  <Text>Activity</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiUser} />
                  <Text>Network</Text>
                  {relationships.length > 0 && (
                    <Badge colorScheme="purple" borderRadius="full">
                      {relationships.length}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiMessageSquare} />
                  <Text>Notes</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiZap} />
                  <Text>Campaigns</Text>
                </HStack>
              </Tab>
              {isCRMAdmin && (
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiFolder} />
                    <Text>Documents</Text>
                  </HStack>
                </Tab>
              )}
              {showRetrTab && (
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiBarChart2} color="teal.500" />
                    <Text>RETR</Text>
                  </HStack>
                </Tab>
              )}
              {isEditing && (
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiEdit2} />
                    <Text>Edit</Text>
                  </HStack>
                </Tab>
              )}
            </TabList>

            <TabPanels>
              {/* OVERVIEW TAB */}
              <TabPanel px={6}>
                <VStack align="stretch" spacing={5}>
                  {/* Contact Details — every field always visible */}
                  <Box>
                    <Heading size="sm" mb={3}>Contact Details</Heading>
                    <Box p={4} bg={infoBG} borderRadius="md">
                      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} fontSize="sm">
                        <OverviewField label="Email">{contact.email}</OverviewField>
                        <OverviewField label="Phone">{contact.phone}</OverviewField>
                        <OverviewField label="Secondary Email">{contact.secondaryEmail}</OverviewField>
                        <OverviewField label="Secondary Phone">{contact.secondaryPhone}</OverviewField>
                        <OverviewField label="Preferred Contact Method">
                          {contact.preferredContactMethod ? (
                            <Text textTransform="capitalize">{contact.preferredContactMethod}</Text>
                          ) : null}
                        </OverviewField>
                        <OverviewField label="Birthday">
                          {formatContactDate(contact.birthday, 'MMMM D, YYYY')}
                        </OverviewField>
                        <OverviewField label="Mailing Address" span={2}>
                          {(contact.mailingAddress?.street || contact.mailingAddress?.city) ? (
                            <Text>
                              {contact.mailingAddress?.street && <>{contact.mailingAddress.street}<br /></>}
                              {contact.mailingAddress?.city && contact.mailingAddress?.state
                                ? `${contact.mailingAddress.city}, ${contact.mailingAddress.state} ${contact.mailingAddress?.zip || ''}`.trim()
                                : contact.mailingAddress?.city || contact.mailingAddress?.state || ''
                              }
                            </Text>
                          ) : null}
                        </OverviewField>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Professional — every field always visible */}
                  <Box>
                    <Heading size="sm" mb={3}>Professional</Heading>
                    <Box p={4} bg={infoBG} borderRadius="md">
                      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} fontSize="sm">
                        <OverviewField label="Company">{contact.company}</OverviewField>
                        <OverviewField label="Job Title">{contact.jobTitle}</OverviewField>
                        <OverviewField label="License #">{contact.licenseNumber}</OverviewField>
                        <OverviewField label="License State">{contact.licenseState}</OverviewField>
                        <OverviewField label="Website" span={2}>
                          {contact.website ? (
                            <Link href={contact.website} isExternal color="blue.500" wordBreak="break-all">
                              {contact.website} <Icon as={FiExternalLink} boxSize={3} />
                            </Link>
                          ) : null}
                        </OverviewField>
                        <OverviewField label="Office Address" span={2}>{contact.officeAddress}</OverviewField>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Record Info — source, status, DNC, ownership, timestamps */}
                  <Box>
                    <Heading size="sm" mb={3}>Record Info</Heading>
                    <Box p={4} bg={infoBG} borderRadius="md">
                      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} fontSize="sm">
                        <OverviewField label="Source">
                          {contact.source ? (
                            <Text textTransform="capitalize">{contact.source}</Text>
                          ) : null}
                        </OverviewField>
                        <OverviewField label="Status">
                          <Badge colorScheme={contact.isActive ? 'green' : 'gray'}>
                            {contact.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </OverviewField>
                        <OverviewField label="Do Not Contact">
                          {(contact.dnc || contact.dncCall || contact.dncEmail || contact.dncSms) ? (
                            <Badge colorScheme="red">
                              {contact.dnc ? 'DO NOT CONTACT' : dncSummary(contact)}
                            </Badge>
                          ) : (
                            <Text>None</Text>
                          )}
                        </OverviewField>
                        {(isCRMAdmin || (contact?.ownerId && contact.ownerId !== currentUser?.uid)) && (
                          <OverviewField label="Owner">{ownerName}</OverviewField>
                        )}
                        {/* Record scope is an admin-only concept — never shown
                            to non-admins (LOs read "company record" as the
                            company claiming their book) */}
                        {isCRMAdmin && (
                          <OverviewField label="Record Scope">
                            <HStack spacing={2}>
                              <Switch
                                size="sm"
                                colorScheme="purple"
                                isChecked={contact.recordScope === 'company'}
                                onChange={handleToggleCompanyScope}
                              />
                              <Badge colorScheme={contact.recordScope === 'company' ? 'purple' : 'gray'}>
                                {contact.recordScope === 'company' ? 'Company' : 'Personal'}
                              </Badge>
                            </HStack>
                          </OverviewField>
                        )}
                        <OverviewField label="Created">{formatContactDate(contact.createdAt)}</OverviewField>
                        <OverviewField label="Last Updated">{formatContactDate(contact.updatedAt)}</OverviewField>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Imported Data — extra spreadsheet columns preserved at import time */}
                  <ImportedDataCard extraFields={contact.extraFields} />

                  {/* Tags */}
                  <Box>
                    <Heading size="sm" mb={3}>Tags</Heading>
                    <HStack spacing={2} flexWrap="wrap">
                      {contact.tags && contact.tags.length > 0 ? (
                        contact.tags.map(tag => (
                          <Badge key={tag} colorScheme="purple">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500">No tags</Text>
                      )}
                    </HStack>
                  </Box>

                  {/* Recent Deals Preview */}
                  {(borrowerDeals.length > 0 || relatedPartyDeals.length > 0) && (
                    <Box>
                      <HStack justify="space-between" mb={3}>
                        <Heading size="sm">Recent Deals</Heading>
                        <Button size="xs" variant="link" onClick={() => setSelectedTab(1)}>
                          View All →
                        </Button>
                      </HStack>
                      
                      {/* Preview: Deals as Borrower */}
                      {borrowerDeals.length > 0 && (
                        <VStack align="stretch" spacing={2} mb={3}>
                          <Text fontSize="xs" fontWeight="semibold" color="gray.600">As Borrower</Text>
                          {borrowerDeals.slice(0, 2).map(deal => (
                            <Box
                              key={deal.id}
                              p={3}
                              bg={infoBG}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={borderColor}
                            >
                              <HStack justify="space-between">
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontWeight="semibold" fontSize="sm">
                                    {deal.dealName}
                                  </Text>
                                  <StageBadge stage={deal.stage} />
                                </VStack>
                                <Button 
                                  size="xs" 
                                  leftIcon={<FiExternalLink />} 
                                  variant="outline"
                                  onClick={() => history.push(`/crm/deals/${deal.id}`)}
                                >
                                  View
                                </Button>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                      
                      {/* Preview: Connected to Deals */}
                      {relatedPartyDeals.length > 0 && (
                        <VStack align="stretch" spacing={2}>
                          <Text fontSize="xs" fontWeight="semibold" color="gray.600">Connected to Deals</Text>
                          {relatedPartyDeals.slice(0, 2).map(deal => (
                            <Box
                              key={deal.id}
                              p={3}
                              bg={infoBG}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={borderColor}
                            >
                              <HStack justify="space-between">
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontWeight="semibold" fontSize="sm">
                                    {deal.dealName}
                                  </Text>
                                  <HStack spacing={2}>
                                    <StageBadge stage={deal.stage} />
                                    <Badge size="xs" colorScheme="purple">
                                      {deal.relationshipRole?.replace(/-/g, ' ')}
                                    </Badge>
                                  </HStack>
                                </VStack>
                                <Button 
                                  size="xs" 
                                  leftIcon={<FiExternalLink />} 
                                  variant="outline"
                                  onClick={() => history.push(`/crm/deals/${deal.id}`)}
                                >
                                  View
                                </Button>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* DEALS TAB */}
              <TabPanel px={6} data-tour="contact-deals">
                <VStack align="stretch" spacing={6}>
                  {dealsLoading ? (
                    <Center py={8}>
                      <Spinner />
                    </Center>
                  ) : (
                    <>
                      {/* Deals as Borrower */}
                      <Box>
                        <HStack justify="space-between" mb={3}>
                          <Heading size="sm">Deals as Borrower</Heading>
                          {borrowerDeals.length > 0 && (
                            <Badge colorScheme="blue" fontSize="sm">
                              {borrowerDeals.length}
                            </Badge>
                          )}
                        </HStack>

                        {borrowerDeals.length === 0 ? (
                          <Box p={6} bg={infoBG} borderRadius="md" textAlign="center">
                            <Icon as={FiBriefcase} boxSize={8} color="gray.300" mb={2} />
                            <Text color="gray.500" fontSize="sm">No deals as borrower yet</Text>
                          </Box>
                        ) : (
                          <Box overflowX="auto" borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                            <Table variant="simple" size="sm">
                              <Thead bg={headerBG}>
                                <Tr>
                                  <Th fontSize="xs">Deal Name</Th>
                                  <Th fontSize="xs">Stage</Th>
                                  <Th fontSize="xs">Amount</Th>
                                  <Th fontSize="xs">Loan Anniversary</Th>
                                  <Th fontSize="xs">Created</Th>
                                  <Th fontSize="xs">Actions</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {borrowerDeals.map(deal => {
                                  const closingDate = deal.estimatedClosingDate;
                                  const isWon = ['loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-won'].includes(deal.stage);
                                  
                                  return (
                                    <Tr key={deal.id} _hover={{ bg: hoverBg }}>
                                      <Td fontWeight="medium" fontSize="sm">{deal.dealName}</Td>
                                      <Td><StageBadge stage={deal.stage} /></Td>
                                      <Td fontSize="sm">{deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}</Td>
                                      <Td fontSize="xs" color="gray.600">
                                        {isWon && closingDate
                                          ? moment(closingDate.toDate ? closingDate.toDate() : closingDate).format('MM/DD/YY')
                                          : '—'}
                                      </Td>
                                      <Td fontSize="xs" color="gray.600">
                                        {deal.createdAt?.seconds 
                                          ? moment(deal.createdAt.seconds * 1000).format('MM/DD/YY')
                                          : '—'}
                                      </Td>
                                      <Td>
                                        <Button 
                                          size="xs" 
                                          leftIcon={<FiExternalLink />} 
                                          variant="outline"
                                          onClick={() => history.push(`/crm/deals/${deal.id}`)}
                                        >
                                          View
                                        </Button>
                                      </Td>
                                    </Tr>
                                  );
                                })}
                              </Tbody>
                            </Table>
                          </Box>
                        )}
                      </Box>

                      {/* Connected to Deals (as Related Party) */}
                      <Box>
                        <HStack justify="space-between" mb={3}>
                          <Heading size="sm">Connected to Deals</Heading>
                          {relatedPartyDeals.length > 0 && (
                            <Badge colorScheme="purple" fontSize="sm">
                              {relatedPartyDeals.length}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.600" mb={3}>
                          Deals where {contact.firstName} is a related party (agent, title company, etc.)
                        </Text>

                        {relatedPartyDeals.length === 0 ? (
                          <Box p={6} bg={infoBG} borderRadius="md" textAlign="center">
                            <Icon as={FiUser} boxSize={8} color="gray.300" mb={2} />
                            <Text color="gray.500" fontSize="sm">Not connected to any deals as a related party</Text>
                          </Box>
                        ) : (
                          <Box overflowX="auto" borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                            <Table variant="simple" size="sm">
                              <Thead bg={headerBG}>
                                <Tr>
                                  <Th fontSize="xs">Deal Name</Th>
                                  <Th fontSize="xs">Stage</Th>
                                  <Th fontSize="xs">Role</Th>
                                  <Th fontSize="xs">Amount</Th>
                                  <Th fontSize="xs">Created</Th>
                                  <Th fontSize="xs">Actions</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {relatedPartyDeals.map(deal => (
                                  <Tr key={deal.id} _hover={{ bg: hoverBg }}>
                                    <Td fontWeight="medium" fontSize="sm">{deal.dealName}</Td>
                                    <Td><StageBadge stage={deal.stage} /></Td>
                                    <Td>
                                      <Badge colorScheme="purple" size="sm">
                                        {deal.relationshipRole?.replace(/-/g, ' ') || 'Related Party'}
                                      </Badge>
                                    </Td>
                                    <Td fontSize="sm">{deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}</Td>
                                    <Td fontSize="xs" color="gray.600">
                                      {deal.createdAt?.seconds 
                                        ? moment(deal.createdAt.seconds * 1000).format('MM/DD/YY')
                                        : '—'}
                                    </Td>
                                    <Td>
                                      <Button 
                                        size="xs" 
                                        leftIcon={<FiExternalLink />} 
                                        variant="outline"
                                        onClick={() => history.push(`/crm/deals/${deal.id}`)}
                                      >
                                        View
                                      </Button>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </VStack>
              </TabPanel>

              {/* ACTIVITY TAB */}
              <TabPanel px={6} data-tour="contact-activity">
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Heading size="sm">Activity Timeline</Heading>
                    <Button
                      size="sm"
                      leftIcon={<FiClipboard />}
                      colorScheme="teal"
                      onClick={onLogActivityOpen}
                    >
                      Log Activity
                    </Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    {deals.length > 0
                      ? `All activity across ${deals.length} deal${deals.length !== 1 ? 's' : ''} and contact communications`
                      : 'All contact communications'}
                  </Text>
                  
                  <UnifiedActivityTimeline
                    contactId={contactId}
                    dealIds={deals.map(d => d.id)}
                    maxHeight="500px"
                    refreshKey={activityRefreshKey}
                  />
                </VStack>
              </TabPanel>

              {/* NETWORK TAB */}
              <TabPanel px={6}>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Heading size="sm">Network Relationships</Heading>
                    <Button
                      size="sm"
                      leftIcon={<FiPlus />}
                      colorScheme="purple"
                      onClick={onAddNetworkOpen}
                    >
                      Add to Network
                    </Button>
                  </HStack>

                  <Text fontSize="sm" color="gray.600">
                    General relationships not tied to specific deals (referral partners, preferred vendors, etc.)
                  </Text>

                  {relationshipsLoading ? (
                    <Center py={8}>
                      <Spinner />
                    </Center>
                  ) : relationships.length === 0 ? (
                    <Center py={8}>
                      <VStack>
                        <Icon as={FiUser} boxSize={12} color="gray.300" />
                        <Text color="gray.500">No network relationships yet</Text>
                      </VStack>
                    </Center>
                  ) : (
                    <VStack align="stretch" spacing={3}>
                      {relationships.map(rel => (
                        <Box
                          key={rel.id}
                          p={4}
                          bg={infoBG}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={borderColor}
                        >
                          <HStack justify="space-between">
                            <HStack spacing={3}>
                              <Avatar
                                size="sm"
                                name={rel.relatedContact ? `${rel.relatedContact.firstName} ${rel.relatedContact.lastName}` : 'Unknown'}
                              />
                              <VStack align="flex-start" spacing={0}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {rel.relatedContact 
                                    ? `${rel.relatedContact.firstName} ${rel.relatedContact.lastName}`
                                    : 'Unknown Contact'}
                                </Text>
                                <Badge size="xs" colorScheme="purple">
                                  {rel.relationshipType?.replace(/-/g, ' ')}
                                </Badge>
                              </VStack>
                            </HStack>
                            <HStack>
                              <Button 
                                size="xs" 
                                variant="outline"
                                onClick={() => {
                                  // Open the related contact in a new drawer
                                  toast({
                                    title: 'View Contact',
                                    description: 'Coming soon - open related contact',
                                    status: 'info',
                                    duration: 2000,
                                  });
                                }}
                              >
                                View
                              </Button>
                              <IconButton
                                icon={<FiTrash2 />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                aria-label="Remove relationship"
                                onClick={async () => {
                                  if (!window.confirm('Remove this network relationship?')) return;
                                  
                                  const result = await removeRelationship(rel.id);
                                  
                                  if (result.success) {
                                    toast({
                                      title: 'Relationship removed',
                                      status: 'success',
                                      duration: 2000,
                                    });
                                  } else {
                                    toast({
                                      title: 'Error',
                                      description: result.error,
                                      status: 'error',
                                      duration: 3000,
                                    });
                                  }
                                }}
                              />
                            </HStack>
                          </HStack>
                          {rel.notes && (
                            <Text fontSize="sm" color="gray.600" mt={2}>
                              {rel.notes}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </TabPanel>

              {/* NOTES TAB */}
              <TabPanel px={6} data-tour="contact-notes">
                <VStack align="stretch" spacing={4}>
                  <Heading size="sm">Contact Notes</Heading>
                  <Text fontSize="sm" color="gray.600">
                    Every note about this person — contact notes plus notes from
                    each deal, clearly badged with where they were logged
                  </Text>
                  
                  <ContactNotes contactId={contactId} dealIds={deals.map(d => d.id)} />
                </VStack>
              </TabPanel>

              {/* CAMPAIGNS TAB */}
              <TabPanel px={6} data-tour="contact-campaigns">
                <ContactCampaignPanel contactId={contact?.id} />
              </TabPanel>

              {/* DOCUMENTS TAB (admin-only for now) */}
              {isCRMAdmin && (
                <TabPanel px={6} data-tour="contact-documents">
                  <DocumentsPanel
                    entityType="contact"
                    entityId={contact?.id}
                    active={selectedTab === DOCUMENTS_TAB_INDEX}
                    defaultCategory={
                      (contact?.contactTypes || []).includes('candidate') ? 'resume' : 'general'
                    }
                  />
                </TabPanel>
              )}

              {/* RETR TAB */}
              {showRetrTab && (
                <TabPanel px={6}>
                  <RetrTabPanel contact={contact} />
                </TabPanel>
              )}

              {/* EDIT TAB */}
              {isEditing && (
                <TabPanel px={6}>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="sm">Edit Contact</Heading>

                    {/* Basic Info */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Basic Information</Text>
                      <VStack spacing={3}>
                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">First Name</FormLabel>
                            <Input
                              value={editedData.firstName}
                              onChange={(e) => setEditedData({ ...editedData, firstName: e.target.value })}
                            />
                          </FormControl>
                          <FormControl maxW="80px">
                            <FormLabel fontSize="sm">MI</FormLabel>
                            <Input
                              value={editedData.middleInitial}
                              onChange={(e) => setEditedData({ ...editedData, middleInitial: e.target.value.toUpperCase() })}
                              maxLength={1}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Last Name</FormLabel>
                            <Input
                              value={editedData.lastName}
                              onChange={(e) => setEditedData({ ...editedData, lastName: e.target.value })}
                            />
                          </FormControl>
                        </HStack>

                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">Email</FormLabel>
                            <Input
                              type="email"
                              value={editedData.email}
                              onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Phone</FormLabel>
                            <Input
                              type="tel"
                              value={editedData.phone}
                              onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
                            />
                          </FormControl>
                        </HStack>

                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">Secondary Email</FormLabel>
                            <Input
                              type="email"
                              value={editedData.secondaryEmail || ''}
                              onChange={(e) => setEditedData({ ...editedData, secondaryEmail: e.target.value })}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Secondary Phone</FormLabel>
                            <Input
                              type="tel"
                              value={editedData.secondaryPhone || ''}
                              onChange={(e) => setEditedData({ ...editedData, secondaryPhone: e.target.value })}
                            />
                          </FormControl>
                        </HStack>

                        <FormControl>
                          <FormLabel fontSize="sm">Birthday</FormLabel>
                          <Input
                            type="date"
                            value={editedData.birthday || ''}
                            onChange={(e) => setEditedData({ ...editedData, birthday: e.target.value })}
                          />
                        </FormControl>
                      </VStack>
                    </Box>

                    <Divider />

                    {/* Mailing Address */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Mailing Address</Text>
                      <Text fontSize="xs" color="gray.500" mb={3}>
                        Contact's residence for communications (not deal property)
                      </Text>
                      <VStack spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="sm">Street Address</FormLabel>
                          <Input
                            value={editedData.mailingAddress?.street || ''}
                            onChange={(e) => setEditedData({ 
                              ...editedData, 
                              mailingAddress: { ...editedData.mailingAddress, street: e.target.value }
                            })}
                            placeholder="123 Main St"
                          />
                        </FormControl>
                        <HStack spacing={3}>
                          <FormControl flex={2}>
                            <FormLabel fontSize="sm">City</FormLabel>
                            <Input
                              value={editedData.mailingAddress?.city || ''}
                              onChange={(e) => setEditedData({ 
                                ...editedData, 
                                mailingAddress: { ...editedData.mailingAddress, city: e.target.value }
                              })}
                            />
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="sm">State</FormLabel>
                            <Input
                              value={editedData.mailingAddress?.state || ''}
                              onChange={(e) => setEditedData({ 
                                ...editedData, 
                                mailingAddress: { ...editedData.mailingAddress, state: e.target.value.toUpperCase() }
                              })}
                              maxLength={2}
                              placeholder="TX"
                            />
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="sm">ZIP</FormLabel>
                            <Input
                              value={editedData.mailingAddress?.zip || ''}
                              onChange={(e) => setEditedData({ 
                                ...editedData, 
                                mailingAddress: { ...editedData.mailingAddress, zip: e.target.value }
                              })}
                              maxLength={10}
                              placeholder="12345"
                            />
                          </FormControl>
                        </HStack>
                      </VStack>
                    </Box>

                    <Divider />

                    {/* Contact Types */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Contact Types</Text>
                      <FormControl>
                        <FormLabel fontSize="sm">Select Types (Multiple Allowed)</FormLabel>
                        <Select
                          isMulti
                          value={editedData.contactTypes?.map(val => ({
                            value: val,
                            label: configContactTypes.find(t => t.value === val)?.label || val
                          }))}
                          onChange={(selectedOptions) => {
                            const types = selectedOptions.map(opt => opt.value);
                            setEditedData({
                              ...editedData,
                              contactTypes: types,
                              primaryContactType: types[0] || 'borrower',
                            });
                          }}
                          options={configContactTypes.map(type => ({
                            value: type.value,
                            label: type.label,
                          }))}
                        />
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          First selected type will be the primary type
                        </Text>
                      </FormControl>
                    </Box>

                    <Divider />

                    {/* Professional Details */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Professional Details</Text>
                      <VStack spacing={3}>
                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">Company</FormLabel>
                            <Input
                              value={editedData.company}
                              onChange={(e) => setEditedData({ ...editedData, company: e.target.value })}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Job Title</FormLabel>
                            <Input
                              value={editedData.jobTitle}
                              onChange={(e) => setEditedData({ ...editedData, jobTitle: e.target.value })}
                            />
                          </FormControl>
                        </HStack>

                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">License Number</FormLabel>
                            <Input
                              value={editedData.licenseNumber}
                              onChange={(e) => setEditedData({ ...editedData, licenseNumber: e.target.value })}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">License State</FormLabel>
                            <Input
                              value={editedData.licenseState}
                              onChange={(e) => setEditedData({ ...editedData, licenseState: e.target.value })}
                              maxLength={2}
                            />
                          </FormControl>
                        </HStack>

                        <FormControl>
                          <FormLabel fontSize="sm">Website</FormLabel>
                          <Input
                            type="url"
                            value={editedData.website}
                            onChange={(e) => setEditedData({ ...editedData, website: e.target.value })}
                            placeholder="https://"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Office Address</FormLabel>
                          <Textarea
                            value={editedData.officeAddress}
                            onChange={(e) => setEditedData({ ...editedData, officeAddress: e.target.value })}
                            rows={2}
                          />
                        </FormControl>
                      </VStack>
                    </Box>

                    <Divider />

                    <Divider />

                    {/* Status Toggles */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Status</Text>
                      <VStack align="stretch" spacing={3}>
                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel fontSize="sm" mb={0}>Active Contact</FormLabel>
                          <Switch
                            isChecked={editedData.isActive}
                            onChange={(e) => setEditedData({ ...editedData, isActive: e.target.checked })}
                            colorScheme="green"
                          />
                        </FormControl>

                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel fontSize="sm" mb={0}>DNC: Everything</FormLabel>
                          <Switch
                            isChecked={editedData.dnc}
                            onChange={(e) => setEditedData({ ...editedData, ...computeDnc(editedData, { everything: e.target.checked }) })}
                            colorScheme="red"
                          />
                        </FormControl>
                        <FormControl display="flex" alignItems="center" justifyContent="space-between" pl={4}>
                          <FormLabel fontSize="sm" mb={0} color="gray.500">Calls / Phone</FormLabel>
                          <Switch
                            isChecked={editedData.dncCall}
                            onChange={(e) => setEditedData({ ...editedData, ...computeDnc(editedData, { call: e.target.checked }) })}
                            colorScheme="red"
                          />
                        </FormControl>
                        <FormControl display="flex" alignItems="center" justifyContent="space-between" pl={4}>
                          <FormLabel fontSize="sm" mb={0} color="gray.500">Emails</FormLabel>
                          <Switch
                            isChecked={editedData.dncEmail}
                            onChange={(e) => setEditedData({ ...editedData, ...computeDnc(editedData, { email: e.target.checked }) })}
                            colorScheme="red"
                          />
                        </FormControl>
                        <FormControl display="flex" alignItems="center" justifyContent="space-between" pl={4}>
                          <FormLabel fontSize="sm" mb={0} color="gray.500">Texts / SMS</FormLabel>
                          <Switch
                            isChecked={editedData.dncSms}
                            onChange={(e) => setEditedData({ ...editedData, ...computeDnc(editedData, { sms: e.target.checked }) })}
                            colorScheme="red"
                          />
                        </FormControl>
                      </VStack>
                    </Box>

                    {/* Tags */}
                    <Box>
                      <Text fontWeight="semibold" mb={3}>Tags</Text>
                      <FormControl>
                        <CreatableSelect
                          isMulti
                          value={(editedData.tags || []).map(val => ({
                            value: val,
                            label: getTag(val)?.label || val
                          }))}
                          onChange={(selectedOptions) => {
                            setEditedData({
                              ...editedData,
                              tags: (selectedOptions || []).map(opt => opt.value),
                            });
                          }}
                          options={editableTagOptions}
                          formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Tags are shared with this contact's deals.
                        </Text>
                      </FormControl>
                    </Box>

                    {/* Custom Fields (if any defined) */}
                    {customContactFields && customContactFields.length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" mb={3}>Custom Fields</Text>
                        <VStack spacing={3}>
                          {customContactFields.map(field => {
                            // Check if field applies to this contact's types
                            const appliesTo = field.appliesTo || ['all'];
                            const isApplicable = appliesTo.includes('all') || 
                              editedData.contactTypes?.some(type => appliesTo.includes(type));

                            if (!isApplicable) return null;

                            return (
                              <FormControl key={field.fieldName}>
                                <FormLabel fontSize="sm">{field.label}</FormLabel>
                                {/* Render field based on type - TODO: Complete all field types */}
                                {field.fieldType === 'text' && (
                                  <Input
                                    value={editedData.customFields?.[field.fieldName] || ''}
                                    onChange={(e) => setEditedData({
                                      ...editedData,
                                      customFields: {
                                        ...editedData.customFields,
                                        [field.fieldName]: e.target.value,
                                      }
                                    })}
                                  />
                                )}
                                {field.fieldType === 'select' && field.options && (
                                  <ChakraSelect
                                    value={editedData.customFields?.[field.fieldName] || ''}
                                    onChange={(e) => setEditedData({
                                      ...editedData,
                                      customFields: {
                                        ...editedData.customFields,
                                        [field.fieldName]: e.target.value,
                                      }
                                    })}
                                  >
                                    <option value="">Select...</option>
                                    {field.options.map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </ChakraSelect>
                                )}
                              </FormControl>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>

      {/* Add Network Relationship Modal */}
      <AddNetworkRelationshipModal
        isOpen={isAddNetworkOpen}
        onClose={onAddNetworkClose}
        contactId={contactId}
        existingRelationships={relationships}
        onRelationshipAdded={() => {
          // Refetch to get the full relationship data with contact info
          refetchRelationships();
          onAddNetworkClose();
        }}
      />

      {/* Quick Add Deal Modal */}
      <QuickAddDeal
        isOpen={isQuickAddDealOpen}
        onClose={onQuickAddDealClose}
        contact={contact}
        onDealCreated={() => {
          toast({
            title: 'Deal created',
            description: 'New deal added successfully',
            status: 'success',
            duration: 2000,
          });
        }}
      />

      {/* Add Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={onTaskModalClose}
        onSave={handleSaveTask}
        prefilledContact={contact}
        crmUsers={crmUsers}
      />

      {/* Email Template Modal */}
      <EmailTemplateModal
        isOpen={isEmailModalOpen}
        onClose={onEmailModalClose}
        contact={contact}
        deal={null}
      />

      {/* Log Activity Modal */}
      <LogActivityModal
        isOpen={isLogActivityOpen}
        onClose={onLogActivityClose}
        contact={contact}
        deals={deals}
        onCreateTask={(taskData) => handleSaveTask(null, taskData)}
        onLogged={() => {
          setActivityRefreshKey((k) => k + 1);
          // Jump to the Activity tab so the new entry is immediately visible
          setSelectedTab(2);
        }}
      />

      {removalDialog}
    </Drawer>
  );
}

